/* Ankle maps built on the Check-In BodyCanvas (zoom / pan / pinch).
   1) OttawaMap — reference image with selectable landmark overlays A–D.
   2) PainMarkerMap — four anatomical views, numbered draggable markers,
      per-marker details. Coordinates are stored relative to each view image. */
import { useState } from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { BodyCanvas } from "@/components/BodyMap";
import { createMarker, updateMarkerList, removeMarkerFromList } from "./ankleMarkers";
import useModalA11y from "@/lib/useModalA11y";

export const MAP_VERSION = "ankle-maps-v1";
const ORANGE = "#f47a20";

// ---- Ottawa ----------------------------------------------------------------
// Polygons in the reference image's pixel space (1560×732), matched to the
// highlighted targets: A/B elongated malleolar edges, C base of 5th, D navicular.
export const OTTAWA_TARGETS = [
  { id: "lat_malleolus", letter: "A", side: "Outside", pts: "405,345 437,342 465,470 455,507 425,508 410,478" },
  { id: "base_5th", letter: "C", side: "Outside", pts: "566,600 640,595 646,632 576,641" },
  { id: "med_malleolus", letter: "B", side: "Inside", pts: "1128,346 1178,346 1197,498 1165,508 1132,498 1122,470" },
  { id: "navicular", letter: "D", side: "Inside", pts: "983,545 1040,527 1053,600 1000,613" },
];

export function OttawaMap({ value = [], onToggle }) {
  const overlay = (
    <svg viewBox="0 0 1560 732" className="absolute inset-0 w-full h-full" aria-hidden="false">
      <rect x="0" y="0" width="1560" height="112" fill="#121212" />
      <rect x="270" y="135" width="260" height="56" rx="10" fill="#121212" />
      <rect x="1040" y="135" width="260" height="56" rx="10" fill="#121212" />
      <text x="400" y="175" fontSize="34" fontWeight="700" fill="#f47a20" textAnchor="middle">Outside</text>
      <text x="1170" y="175" fontSize="34" fontWeight="700" fill="#f47a20" textAnchor="middle">Inside</text>
      {OTTAWA_TARGETS.map((t) => {
        const on = value.includes(t.id);
        return (
          <polygon key={t.id} points={t.pts} role="checkbox" aria-checked={on} tabIndex={0} aria-label={`${t.letter}: ${t.id}`}
            data-testid={`ottawa-target-${t.id}`}
            onClick={(e) => { e.stopPropagation(); onToggle(t.id); }}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle(t.id))}
            fill={on ? ORANGE : "rgba(244,122,32,0.25)"} stroke={on ? "#fff" : ORANGE} strokeWidth={on ? 6 : 3}
            style={{ cursor: "pointer", pointerEvents: "auto", transition: "fill 150ms" }} />
        );
      })}
    </svg>
  );
  return (
    <div data-testid="ottawa-map">
      <BodyCanvas marks={[]} activeId={null} onOpen={() => {}} onAdd={() => {}} canPlace={() => false} testid="ottawa"
        src="/ankle/ottawa.png" alt="Ottawa ankle rules landmarks — outside and inside views" overlay={overlay} />
      <div className="text-[11px] text-zinc-500 mt-1">Tap a highlighted area to select or deselect it · scroll/pinch to zoom · double-tap to reset.</div>
    </div>
  );
}

// ---- Pain markers ------------------------------------------------------------
export const VIEWS = [
  { id: "front", label: "Front", src: "/ankle/view-front.png", region: "anterior",
    poly: [[0.18, 0.02], [0.82, 0.02], [0.9, 0.45], [0.98, 0.8], [0.85, 0.98], [0.08, 0.98], [0.02, 0.8], [0.1, 0.45]] },
  { id: "back", label: "Back", src: "/ankle/view-back.png", region: "posterior",
    poly: [[0.15, 0.02], [0.85, 0.02], [0.92, 0.6], [0.9, 0.97], [0.1, 0.97], [0.08, 0.6]] },
  { id: "outside", label: "Outside", src: "/ankle/view-outside.png", region: "lateral",
    poly: [[0.30, 0.03], [0.58, 0.03], [0.60, 0.42], [0.98, 0.55], [0.98, 0.88], [0.55, 0.92], [0.06, 0.90], [0.02, 0.60], [0.18, 0.48]] },
  { id: "inside", label: "Inside", src: "/ankle/view-inside.png", region: "medial",
    poly: [[0.42, 0.03], [0.72, 0.03], [0.96, 0.20], [0.98, 0.55], [0.94, 0.90], [0.45, 0.92], [0.02, 0.88], [0.02, 0.58], [0.40, 0.45]] },
];

function inPoly(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function ViewCanvas({ view, markers, activeId, foot, onAdd, onMove, onOpen, big }) {
  const marks = markers.map((m, i) => ({ ...m, number: i + 1 })).filter((m) => m.view === view.id && m.foot === foot);
  return (
    <div className={big ? "max-w-[560px] mx-auto" : ""}>
      <BodyCanvas marks={marks} activeId={activeId} onOpen={onOpen} testid={`ankle-${view.id}`}
        src={view.src} alt={`${view.label} view of the foot and ankle`} resolveView={() => view.id}
        canPlace={(x, y) => inPoly([x, y], view.poly)} onMove={onMove}
        maxImgHeight={big ? "72vh" : "min(52vh, 380px)"}
        onAdd={(p) => onAdd({ ...p, view: view.id, foot })} />
    </div>
  );
}

function IntensityControl({ value, onChange, mid }) {
  return (
    <div data-testid={`marker-intensity-${mid}`}>
      <div className="text-xs text-zinc-400 mb-1">Pain intensity now</div>
      <div className="grid grid-cols-10 gap-1">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`Pain intensity ${n}`}
            onClick={() => onChange(n)} data-testid={`marker-intensity-${mid}-${n}`}
            className={`h-9 rounded text-xs font-semibold border ${value === n ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300 hover:border-[#f47a20]/60"}`}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1"><span>1 · Very mild</span><span>10 · Worst pain imaginable</span></div>
      <button type="button" role="radio" aria-checked={value === 0} onClick={() => onChange(0)} data-testid={`marker-intensity-${mid}-none`}
        className={`mt-2 text-xs px-3 py-1.5 rounded border ${value === 0 ? "bg-[#f47a20]/20 border-[#f47a20] text-white" : "border-white/15 text-zinc-300"}`}>No pain at the moment</button>
    </div>
  );
}

export function MarkerDetails({ marker, index, painOptions, maxTypes, onUpdate, onRemove }) {
  const toggleType = (oid) => {
    const cur = marker.types || [];
    let next = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
    if (next.length > maxTypes) next = next.slice(next.length - maxTypes);
    onUpdate({ types: next });
  };
  const view = VIEWS.find((v) => v.id === marker.view);
  return (
    <div className="fl-card p-4 border border-[#f47a20]/40 space-y-3" data-testid={`marker-details-${index + 1}`}>
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-[#f47a20] text-black text-sm font-bold flex items-center justify-center">{index + 1}</span>
        <div className="flex-1 text-sm">Marker {index + 1} · {view?.label} view · <span className="capitalize">{marker.foot}</span> foot</div>
        <button type="button" onClick={onRemove} className="text-red-400 text-xs inline-flex items-center gap-1" data-testid={`marker-remove-${index + 1}`}><X size={12} /> Remove</button>
      </div>
      <div className="flex gap-2 text-xs">
        {["left", "right"].map((f) => (
          <button key={f} type="button" onClick={() => onUpdate({ foot: f })} data-testid={`marker-foot-${index + 1}-${f}`}
            className={`px-3 py-1 rounded border capitalize ${marker.foot === f ? "bg-[#f47a20]/20 border-[#f47a20] text-white" : "border-white/15 text-zinc-300"}`}>{f} foot</button>
        ))}
      </div>
      <div>
        <div className="text-xs text-zinc-400 mb-1">Pain type here (choose up to {maxTypes})</div>
        <div className="flex flex-wrap gap-1.5">
          {painOptions.map((o) => {
            const on = (marker.types || []).includes(o.id);
            return <button key={o.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggleType(o.id)} data-testid={`marker-type-${index + 1}-${o.id}`}
              className={`text-xs px-2.5 py-1.5 rounded border text-left ${on ? "bg-[#f47a20]/20 border-[#f47a20] text-white" : "border-white/15 text-zinc-300"}`}>{o.label}</button>;
          })}
        </div>
      </div>
      <IntensityControl value={marker.intensity ?? null} onChange={(v) => onUpdate({ intensity: v })} mid={index + 1} />
      <input className="fl-input text-sm w-full" placeholder="Optional comment about this spot" value={marker.comment || ""} maxLength={500}
        onChange={(e) => onUpdate({ comment: e.target.value })} data-testid={`marker-comment-${index + 1}`} />
    </div>
  );
}

export function PainMarkerMap({ markers, onChange, painOptions, maxTypes = 2 }) {
  const [view, setView] = useState("outside");
  const [foot, setFoot] = useState("right");
  const [activeId, setActiveId] = useState(null);
  const [big, setBig] = useState(false);
  const dialogRef = useModalA11y({ open: big, onClose: () => setBig(false) });
  const v = VIEWS.find((x) => x.id === view);

  const add = (p) => {
    const m = createMarker(p.view, p.foot, p.x_pct, p.y_pct);
    onChange([...markers, m]); setActiveId(m.id);
  };
  const move = (id, p) => onChange(updateMarkerList(markers, id, { x_pct: +p.x_pct.toFixed(4), y_pct: +p.y_pct.toFixed(4) }));
  const update = (id, patch) => onChange(updateMarkerList(markers, id, patch));
  const remove = (id) => { onChange(removeMarkerFromList(markers, id)); if (activeId === id) setActiveId(null); };
  const open = (id) => { const m = markers.find((x) => x.id === id); if (m) { setView(m.view); setFoot(m.foot); } setActiveId(id); };

  const controls = (sfx = "") => (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <div className="flex gap-1" role="tablist" aria-label="View">
        {VIEWS.map((x) => <button key={x.id} type="button" role="tab" aria-selected={view === x.id} onClick={() => setView(x.id)} data-testid={`ankle-view-${x.id}${sfx}`}
          className={`px-3 py-1.5 rounded border ${view === x.id ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300"}`}>{x.label}</button>)}
      </div>
      <div className="flex gap-1 ml-auto" role="radiogroup" aria-label="Foot">
        {["left", "right"].map((f) => <button key={f} type="button" role="radio" aria-checked={foot === f} onClick={() => setFoot(f)} data-testid={`ankle-foot-${f}${sfx}`}
          className={`px-3 py-1.5 rounded border capitalize ${foot === f ? "bg-[#f47a20]/20 border-[#f47a20] text-white" : "border-white/15 text-zinc-300"}`}>{f} foot</button>)}
      </div>
      <button type="button" onClick={() => setBig((b) => !b)} className="fl-btn-ghost text-xs py-1.5" aria-label={big ? "Close expanded view" : "Expand map"} data-testid={`ankle-map-expand${sfx}`}>
        {big ? <Minimize2 size={12} /> : <Maximize2 size={12} />} {big ? "Close" : "Expand"}
      </button>
    </div>
  );
  const canvas = (big2) => <ViewCanvas view={v} markers={markers} activeId={activeId} foot={foot} onAdd={add} onMove={move} onOpen={open} big={big2} />;
  const countHere = markers.filter((m) => m.view === view && m.foot === foot).length;

  return (
    <div data-testid="pain-marker-map">
      {controls()}
      <div className="mt-2">{!big && canvas(false)}</div>
      <div className="text-[11px] text-zinc-500 mt-1">Tap the foot to add a numbered marker ({countHere} on this view) · drag a marker to move it · scroll/pinch to zoom.</div>
      {markers.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="marker-list">
          {markers.map((m, i) => (
            <div key={m.id}>
              <button type="button" onClick={() => setActiveId(activeId === m.id ? null : m.id)} data-testid={`marker-row-${i + 1}`}
                className={`w-full text-left text-xs flex items-center gap-2 px-3 py-2 rounded border ${activeId === m.id ? "border-[#f47a20]" : "border-white/10"}`}>
                <span className="w-5 h-5 rounded-full bg-[#f47a20] text-black font-bold flex items-center justify-center text-[10px]">{i + 1}</span>
                <span className="flex-1">{VIEWS.find((x) => x.id === m.view)?.label} · <span className="capitalize">{m.foot}</span> foot{m.types?.length ? ` · ${m.types.length} type(s)` : ""}{m.intensity != null ? ` · intensity ${m.intensity}` : ""}</span>
                <span className="text-zinc-500">{activeId === m.id ? "Hide" : "Edit"}</span>
              </button>
              {activeId === m.id && <div className="mt-2"><MarkerDetails marker={m} index={i} painOptions={painOptions} maxTypes={maxTypes} onUpdate={(p) => update(m.id, p)} onRemove={() => remove(m.id)} /></div>}
            </div>
          ))}
        </div>
      )}
      {big && (
        <div className="fixed inset-0 z-[90] bg-black/90 p-3 overflow-auto" role="dialog" aria-modal="true" aria-label="Expanded pain map" ref={dialogRef} data-testid="ankle-map-fullscreen">
          <div className="max-w-3xl mx-auto fl-card p-4 space-y-3">
            {controls("-fs")}
            {canvas(true)}
            <div className="text-[11px] text-zinc-500">Selections are kept when you close this view.</div>
          </div>
        </div>
      )}
    </div>
  );
}
